import Divider from 'components/divider';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { Row } from './components/row';
import { TotalRow } from './components/total-row';
import { getSavingsItemBadge } from './lib/getSavingsItemBadge';
import { getSavingsItemTitle } from './lib/getSavingsItemTitle';
import { getShippingRowConfig } from './lib/getShippingRowConfig';
import {
  PriceSummaryConfig,
  PriceSummaryData,
  PriceSummaryDiscountItem,
  PriceSummaryDiscountType,
} from './types';

interface Props {
  config: PriceSummaryConfig;
  summary: PriceSummaryData;
}

const SAVINGS_DISPLAY_ORDER: PriceSummaryDiscountType[] = [
  'offer',
  'dynamic_bundle',
  'subscription',
  'coupon',
  'savings_plan',
  // 'referal' is handled separately
];

/**
 * We show friend referral discount separately, hence we need to remove it from the list and adjust total savings accordingly
 * This function also orders saving items according to experiment requirements
 */
const patchSummary = (
  summary: PriceSummaryData
): PriceSummaryData & { referralSaving?: PriceSummaryDiscountItem } => {
  const referralSaving = summary.savingsItems.find((item) => item.type === 'referal');

  // will save same type items in case we have multiple ones, ex: multiple dynamic_bundle
  const savingsItems = SAVINGS_DISPLAY_ORDER.flatMap((type) =>
    summary.savingsItems.filter((item) => item.type === type)
  );

  return {
    ...summary,
    savingsItems,
    referralSaving,
    savingsTotal: summary.savingsTotal - (referralSaving?.amount ?? 0),
    subtotalAfterDiscounts: summary.subtotalAfterDiscounts + (referralSaving?.amount ?? 0),
  };
};

export const PriceSummaryContent: FC<Props> = ({ config, summary: originalSummary }) => {
  const { formatMessage } = useIntl();
  const summary = patchSummary(originalSummary);
  const hasSavings = summary.savingsTotal > 0;
  const shippingRowConfig = getShippingRowConfig({
    config,
    formatMessage,
  });

  const shippingRow = (
    <Row
      dataTest={{
        container: 'price-row-shipping',
        price: 'shipping-price',
      }}
      price={
        config.shippingPriceContent ?? shippingRowConfig.priceContent ?? shippingRowConfig.price
      }
      title={shippingRowConfig.title}
    />
  );

  const subTotalRow = (
    <Row
      dataTest={{
        container: 'price-row-sub-total',
        price: 'price-row-sub-total-message',
        title: 'formatted-message-sub-total',
      }}
      price={summary.subtotal}
      title={formatMessage({ id: 'cart:items-total' })}
    />
  );

  const subTotalAfterDiscountsRow = hasSavings && (
    <Row
      dataTest={{
        container: 'price-row-subtotal-after-discounts',
        price: 'price-row-subtotal-after-discounts-price',
        title: 'formatted-message-subtotal-after-discounts',
      }}
      price={summary.subtotalAfterDiscounts}
      title={formatMessage({ id: 'cart:sub-total-with-disscounts' })}
    />
  );

  const creditRow = summary.creditAmount > 0 && (
    <Row
      dataTest={{
        container: 'price-row-credit',
        price: 'price-row-credit-price',
        title: 'formatted-message-credit',
      }}
      price={-summary.creditAmount}
      title={formatMessage({ id: 'cart:savings:credit' })}
    />
  );

  const friendReferralRow = summary.referralSaving && (
    <Row
      dataTest={{
        container: 'price-row-friend-referral',
        price: 'price-row-friend-referral-price',
        title: 'formatted-message-friend-referral',
      }}
      price={-summary.referralSaving.amount}
      title={formatMessage({ id: 'cart:discount:Referal' })}
    />
  );

  return (
    <div className="flex flex-col" data-test="price-summary">
      {subTotalRow}

      {hasSavings && (
        <>
          <Divider />
          <SavingsWithBreakdown config={config} summary={summary} />
        </>
      )}

      {hasSavings && (
        <>
          <Divider />
          {subTotalAfterDiscountsRow}
        </>
      )}

      <Divider />
      {shippingRow}
      {!config.hideTotal && creditRow}
      {friendReferralRow}
      {!config.hideTotal && (
        <>
          <Divider />
          <TotalRow
            className="mt-1"
            summary={summary}
            title={formatMessage({ id: 'cart:total:incl-vat' })}
          />
        </>
      )}
    </div>
  );
};

export const SavingsWithBreakdown: FC<{
  config: PriceSummaryConfig;
  summary: PriceSummaryData;
}> = ({ config, summary }) => {
  const { formatMessage } = useIntl();
  const { savingsItems } = summary;
  if (savingsItems.length === 0) {
    return null;
  }

  if (savingsItems.length === 1) {
    const [savingsItem] = savingsItems;
    return (
      <Row
        dataTest={{
          container: 'savings-header',
          row: 'savings-price-row',
          subLine: `savings-entry-${savingsItem.type}-sub-line`,
          toggle: 'savings-toggle-button',
        }}
        badge={getSavingsItemBadge(savingsItem)}
        price={-summary.savingsTotal}
        subLine={config.savingsSubLines?.[savingsItem.type]}
        title={getSavingsItemTitle(savingsItem, { formatMessage })}
      />
    );
  }

  return (
    <Row
      expanded={config.isSavingsExpanded}
      dataTest={{
        breakdown: 'expanded-savings',
        container: 'savings-header',
        row: 'savings-price-row',
        toggle: 'savings-toggle-button',
      }}
      breakdown={savingsItems.map((item) => ({
        badge: getSavingsItemBadge(item),
        dataTest: {
          price: `savings-entry-${item.type}-price`,
          root: `savings-entry-${item.type}`,
          subLine: `savings-entry-${item.type}-sub-line`,
          title: `savings-entry-${item.type}-title`,
        },
        price: -item.amount,
        subLine: config.savingsSubLines?.[item.type],
        title: getSavingsItemTitle(item, { formatMessage }),
      }))}
      price={-summary.savingsTotal}
      title={formatMessage({ id: 'cart:savings:total' })}
    />
  );
};
